import { useState } from 'react'
import { TenantImageEnum, showNotification, useAssetsContext } from 'summon-ui'
import { useTranslation } from 'summon-ui/intl'
import { Button, Flex, Paper, Stack, Modal, Title, Text, Group } from 'summon-ui/mantine'
import { BridgeLink, NetworkSelector } from '@components'
import { TENANT_CONTRACTS } from '@config'
import { ITEMS_BOUND_ABI } from '@constants/Abi/itemsBoundAbi'
import useClaim from '@features/home/hooks/useClaim'
import { ClaimableItemType } from '@generated/generates'
import { useMintTypeBased } from '@hooks'
import { MintType } from '@hooks/useMintTypeBased'

interface Props {
  id: string
  nonce: number
  signature: string
  randomData: string
  isMainnet: boolean
}

const Claimable = ({ id, nonce, signature, randomData, isMainnet }: Props) => {
  const t = useTranslation()
  const { getTenantImage } = useAssetsContext()
  const misteryBoxImage = getTenantImage(TenantImageEnum.MisteryBox)
  const [isLoading, setIsLoading] = useState(false)
  const [mintType, setMintType] = useState<MintType>(MintType.Eth)
  const [isMintModalOpen, setIsMintModalOpen] = useState(false)
  const { markAsClaimed } = useClaim(ClaimableItemType.Tier)
  const { mint } = useMintTypeBased({
    mintType,
    functionName: 'mint',
    args: [randomData, 1, false, nonce, signature],
    contract: isMainnet ? TENANT_CONTRACTS.mainnet.items : TENANT_CONTRACTS.testnet.items,
    abi: ITEMS_BOUND_ABI,
    isMainnet
  })

  const handleCloseMintModal = () => {
    setIsMintModalOpen(false)
  }

  const claim = async () => {
    try {
      setIsLoading(true)
      const tx = await mint()
      await markAsClaimed(id, tx)
      showNotification({
        variant: 'success',
        message: t('Item minted successfully!')
      })
      setIsLoading(false)
      handleCloseMintModal()
    } catch (error) {
      setIsLoading(false)
      showNotification({
        variant: 'danger',
        message: t('Could not mint the item')
      })
    }
  }

  return (
    <>
      <Stack gap='xs' w={150}>
        <Paper withBorder radius='md' maw={150} p='xs'>
          <img className='h-full' src={misteryBoxImage as string} alt='Mystery Box' />
        </Paper>
        <Flex justify='center' align='center' className='h-12'>
          <Button fullWidth onClick={() => setIsMintModalOpen(true)}>
            {t('Claim')}
          </Button>
        </Flex>
      </Stack>
      <Modal opened={isMintModalOpen} onClose={handleCloseMintModal} size='xs'>
        <Stack p='md' gap='0'>
          <Title order={3}>{t('Select Gas Option')}</Title>
          <Text>{t('Pay the gas fee with ETH or USDC')}</Text>
          <BridgeLink />
          <Group mt='lg' wrap='nowrap'>
            <NetworkSelector setMintType={setMintType} />
            <Button fullWidth onClick={claim} loading={isLoading}>
              {t('Claim')}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}

export default Claimable
